/**
 * mSTEM Phase 6 (Khối Mở rộng) extension-authoring storage helper.
 *
 * MOCK-ONLY (DR-00): persists user-declared devices + blocks to localStorage
 * under the `mstem.*` namespace so the Block-Builder / Device-Declaration
 * demos feel real within a session. NO backend, NO real registry, NO codegen.
 *
 * Reuses the generic guard helpers from mstem-storage.js — NOT a duplicate:
 *   - readJsonList shape-guards parsed JSON (every entry must have id+name
 *     strings, else returns null) → defends against a tampered localStorage
 *     entry injecting unknown fields into the reducer / device library
 *     (threat T-06-01-01, RESEARCH Pitfall 2).
 *   - writeJsonList wraps JSON.stringify + safeWrite (try/catch quota guard).
 *
 * Icon size cap (threat T-06-01-02, RESEARCH Pitfall 2): FileReader data-URL
 * icons can blow the ~5-10MB localStorage budget. `iconWithinCap` enforces a
 * 100KB ceiling; the EXT-01 wizard (06-02) falls back to a preset icon +
 * `extDeviceSaveError`/`errIcon` toast when an upload exceeds the cap.
 */
import {readJsonList, writeJsonList} from './mstem-storage.js';

const CUSTOM_DEVICES_KEY = 'mstem.customDevices';
const CUSTOM_BLOCKS_KEY = 'mstem.customBlocks';
const MAX_ICON_BYTES = 100 * 1024; // 100KB data-URL ceiling (Pitfall 2)

const readCustomDevices = () => readJsonList(CUSTOM_DEVICES_KEY) || [];

const writeCustomDevices = list => writeJsonList(CUSTOM_DEVICES_KEY, list);

const readCustomBlocks = () => readJsonList(CUSTOM_BLOCKS_KEY) || [];

const writeCustomBlocks = list => writeJsonList(CUSTOM_BLOCKS_KEY, list);

// True when the icon data-URL is a string within the 100KB cap. Callers that
// receive `false` should drop the user icon and fall back to a preset glyph.
const iconWithinCap = dataUrl => (typeof dataUrl === 'string' && dataUrl.length <= MAX_ICON_BYTES);

// Idempotent upsert: replace any existing entry with the same id, then append.
const addCustomDevice = function (device) {
    const next = readCustomDevices()
        .filter(d => d.id !== device.id)
        .concat(device);
    writeCustomDevices(next);
    return next;
};

const addCustomBlock = function (block) {
    const next = readCustomBlocks()
        .filter(b => b.id !== block.id)
        .concat(block);
    writeCustomBlocks(next);
    return next;
};

// UC-43 / UC-47 management (Phase 6 follow-up, MOCK-ONLY): remove an entry by
// id from the persisted list and return the new list. Idempotent — removing a
// missing id is a no-op that still rewrites the (unchanged) list.
const removeCustomDevice = function (id) {
    const next = readCustomDevices().filter(d => d.id !== id);
    writeCustomDevices(next);
    return next;
};

const removeCustomBlock = function (id) {
    const next = readCustomBlocks().filter(b => b.id !== id);
    writeCustomBlocks(next);
    return next;
};

export {
    readCustomDevices,
    writeCustomDevices,
    readCustomBlocks,
    writeCustomBlocks,
    iconWithinCap,
    addCustomDevice,
    addCustomBlock,
    removeCustomDevice,
    removeCustomBlock,
    CUSTOM_DEVICES_KEY,
    CUSTOM_BLOCKS_KEY,
    MAX_ICON_BYTES
};
